import type { AudioCodecSpecificData } from '../types/audioCodecSpecificData';
import { invariant } from '../util/invariant';
import type { AudioSpecificConfig, DecoderSpecificInfo, EditListBox, ElementaryStreamDescriptorBox, HandlerBox, MovieHeaderBox, Mp4aBox, Mp4Box, SampleDescriptionBox, TimeToSampleBox, TrackHeaderBox } from './parser';

function isAudioSpecificConfig(info: DecoderSpecificInfo): info is AudioSpecificConfig {
  return 'audioObjectType' in info && typeof info.audioObjectType === 'number';
}

export interface TrakBoxes {
  trackId: number;
  mediaStartPtsOffset: number;
  timescale: number;
  handlerType: string;
  stblBox: Mp4Box;
  stsdBox: SampleDescriptionBox;
  sttsBox: TimeToSampleBox;
}

export function readTrakBoxes(trakBox: Mp4Box): TrakBoxes {
  const tkhdBox = trakBox.children.find(b => b.type === 'tkhd') as TrackHeaderBox | undefined;
  invariant(tkhdBox, 'Could not find tkhd box');

  let mediaStartPtsOffset = 0;
  const edtsBox = trakBox.children.find(b => b.type === 'edts');
  if (edtsBox !== undefined) {
    const elstBox = edtsBox.children.find(b => b.type === 'elst') as EditListBox | undefined;
    const firstEntry = elstBox?.entries[0];
    if (firstEntry !== undefined) {
      mediaStartPtsOffset = firstEntry.media_time;
    }
  }

  const mdiaBox = trakBox.children.find(b => b.type === 'mdia');
  invariant(mdiaBox, 'Could not find mdia box');

  const mdhdBox = mdiaBox.children.find(b => b.type === 'mdhd') as MovieHeaderBox | undefined;
  invariant(mdhdBox, 'Could not find mdhd box');

  const hdlrBox = mdiaBox.children.find(b => b.type === 'hdlr') as HandlerBox | undefined;
  invariant(hdlrBox, 'Could not find hdlr box');

  const minfBox = mdiaBox.children.find(b => b.type === 'minf');
  invariant(minfBox, 'Could not find minf box');

  const stblBox = minfBox.children.find(b => b.type === 'stbl');
  invariant(stblBox, 'Could not find stbl box');

  const stsdBox = stblBox.children.find(b => b.type === 'stsd') as SampleDescriptionBox | undefined;
  invariant(stsdBox, 'Could not find stsd box');

  const sttsBox = stblBox.children.find(b => b.type === 'stts') as TimeToSampleBox | undefined;
  invariant(sttsBox, 'Could not find stts box');

  return {
    trackId: tkhdBox.track_id,
    mediaStartPtsOffset,
    timescale: mdhdBox.timescale,
    handlerType: hdlrBox.handler_type,
    stblBox,
    stsdBox,
    sttsBox,
  };
}

export function findMp4aBox(stsdBox: SampleDescriptionBox): Mp4aBox | undefined {
  return stsdBox.children.find(b => b.type === 'mp4a') as Mp4aBox | undefined;
}

export interface Mp4aSampleEntryInfo {
  sampleRate: number;
  bitsPerSample: number;
  channelCount: number;
  codecSpecificData: AudioCodecSpecificData | null;
}

export function readMp4aSampleEntry(mp4aBox: Mp4aBox, timescale: number): Mp4aSampleEntryInfo {
  let sampleRate = mp4aBox.samplerate;
  let codecSpecificData: AudioCodecSpecificData | null = null;

  // We have observed that the channel count in the mp4a box is sometimes
  // incorrect. To avoid issues when this is the case, we try to extract
  // the codec specific information from the esds box
  let channelCount = mp4aBox.channelcount;
  const esdsBox = mp4aBox.children.find(b => b.type === 'esds') as ElementaryStreamDescriptorBox | undefined;
  if (esdsBox) {
    const audioSpecificConfig = esdsBox.es.decConfigDescr.decSpecificInfo.find(isAudioSpecificConfig);
    if (audioSpecificConfig) {
      switch(audioSpecificConfig.channelConfiguration) {
        case 1:
        case 2:
        case 3:
        case 4:
        case 5:
        case 6: channelCount = audioSpecificConfig.channelConfiguration; break;
        case 7: channelCount = 8; break;
        case 11: channelCount = 7; break;
        case 12: channelCount = 8; break;
        case 14: channelCount = 8; break;
        default: break;
      }
      codecSpecificData = {
        type: 'aac',
        audioObjectType: audioSpecificConfig.audioObjectType,
        samplingFrequencyIndex: audioSpecificConfig.samplingFrequencyIndex,
        channelConfiguration: audioSpecificConfig.channelConfiguration,
      };
    }
  }

  // For sample rate values that cannot be represented in & 16 bit integer,
  // it is expected that the samplerate property is set to 0. In such a case,
  // the sample rate should be extracted from the Elementary Stream Descriptor.
  // However, in practice it is often the case that the timescale has the correct
  // value, at least for files generated by ffmpeg. So as a quick fix, we try to
  // default to that if we find a samplerate of 0 with the timescale being one of
  // the valid AAC sample rates that are too large for 16 bits
  if (sampleRate === 0) {
    if (timescale === 88200 || timescale === 96000 || timescale === 176400 || timescale === 192000) {
      sampleRate = timescale;
    }
  }

  return {
    sampleRate,
    bitsPerSample: mp4aBox.samplesize,
    channelCount,
    codecSpecificData,
  };
}
